import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/layout/SiteLayout";
import Seo, { SITE_URL } from "@/components/base/Seo";
import type { Business } from "@/types/directory";
import { getBusinessBySlug, recordBusinessView } from "@/services/directoryService";
import BusinessHero from "./components/BusinessHero";
import BusinessAbout from "./components/BusinessAbout";
import BusinessContactCard from "./components/BusinessContactCard";
import BusinessReviews from "./components/BusinessReviews";
import BusinessCredentials from "./components/BusinessCredentials";
import SimilarBusinesses from "./components/SimilarBusinesses";

export default function BusinessPage() {
  const { slug = "" } = useParams();
  const [business, setBusiness] = useState<Business | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getBusinessBySlug(slug)
      .then((result) => {
        if (active) setBusiness(result);
        // Record an analytics view for live, API-backed listings.
        if (active && result && /^\d+$/.test(result.id)) {
          recordBusinessView(result.id);
        }
      })
      .catch(() => {
        if (active) setBusiness(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [slug]);

  if (loading) {
    return (
      <SiteLayout>
        <div className="mx-auto flex w-full max-w-[1400px] items-center justify-center px-6 py-32">
          <div className="flex flex-col items-center gap-4 text-foreground-500">
            <span className="flex h-10 w-10 animate-spin items-center justify-center rounded-full border-2 border-background-300 border-t-primary-500" />
            <p className="text-sm">Loading business profile...</p>
          </div>
        </div>
      </SiteLayout>
    );
  }

  if (!business) {
    return (
      <SiteLayout>
        <section className="mx-auto flex w-full max-w-[1400px] flex-col items-center justify-center px-6 py-24 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-store-3-line text-3xl" />
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">
            Business not found
          </h1>
          <p className="mt-3 max-w-md text-sm text-foreground-600">
            This listing may have been removed or the link is incorrect. Browse the directory to find
            other local businesses.
          </p>
          <Link
            to="/directory"
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            Back to directory
          </Link>
        </section>
      </SiteLayout>
    );
  }

  const shortLocation = business.address.split(",").slice(-3).join(",").trim();
  const localBusinessJsonLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: business.name,
    image: business.cover,
    description: business.description,
    telephone: business.phone,
    email: business.email,
    url: business.website || `${SITE_URL}/business/${business.slug}`,
    address: {
      "@type": "PostalAddress",
      streetAddress: business.address,
      addressLocality: shortLocation,
      addressRegion: "Karnataka",
      addressCountry: "IN",
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: business.rating,
      reviewCount: business.reviewCount,
    },
    openingHoursSpecification: business.hours
      .filter((entry) => !entry.closed)
      .map((entry) => ({
        "@type": "OpeningHoursSpecification",
        dayOfWeek: entry.day,
        opens: entry.open,
        closes: entry.close,
      })),
  };

  return (
    <SiteLayout>
      <Seo
        title={`${business.name} - ${business.subcategory} in ${shortLocation}`}
        description={`${business.tagline}. Contact ${business.name} in ${shortLocation} — see reviews, working hours, photos and direct call, WhatsApp and enquiry options.`}
        keywords={`${business.name}, ${business.subcategory} ${shortLocation}, ${business.subcategory} coastal Karnataka, ${business.name} contact`}
        path={`/business/${business.slug}`}
        image={business.cover}
        type="article"
        jsonLd={localBusinessJsonLd}
      />
      <BusinessHero business={business} />
      <section className="mx-auto w-full max-w-[1400px] px-6 py-10 md:py-12">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_380px]">
          <div className="flex flex-col gap-8">
            <BusinessAbout business={business} />
            <BusinessCredentials business={business} />
            <BusinessReviews business={business} />
          </div>
          <div className="flex flex-col gap-6">
            <BusinessContactCard business={business} />
          </div>
        </div>
      </section>
      <SimilarBusinesses business={business} />
    </SiteLayout>
  );
}